import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import type { MacroBriefCardData, MacroBriefIndicator } from "store/types";

interface Props {
  data?: MacroBriefCardData;
  loading?: boolean;
}

function formatIndicatorValue(value: number, prefix: string): string {
  if (prefix === "%") return `${value.toFixed(2)}%`;
  if (prefix === "$") return `$${value.toFixed(2)}`;
  if (value >= 1_000) return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
  return value.toFixed(3);
}

function changeColor(current: number, prior: number): string {
  if (current > prior) return "#059669";
  if (current < prior) return "#DC2626";
  return "#6B7280";
}

function changeArrow(current: number, prior: number): string {
  if (current > prior) return "\u2191";
  if (current < prior) return "\u2193";
  return "\u2192";
}

function changeText(current: number, prior: number, prefix: string): string {
  const diff = current - prior;
  const sign = diff >= 0 ? "+" : "";
  if (prefix === "%") return `${sign}${diff.toFixed(2)}%`;
  if (prefix === "$") return `${sign}$${diff.toFixed(2)}`;
  if (Math.abs(diff) >= 1_000) return `${sign}${diff.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  return `${sign}${diff.toFixed(3)}`;
}

export const MacroBriefCard: React.FC<Props> = ({ data, loading }) => {

  if (loading) {
    return (
      <Box sx={{ p: "16px 8px 16px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  const selectedIndicators: MacroBriefIndicator[] = data ?? [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#6B7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 1.5
        }}
      >
        Macro Brief
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {selectedIndicators.map((indicator) => {
          const color = changeColor(indicator.value, indicator.prior_value);
          const arrow = changeArrow(indicator.value, indicator.prior_value);
          const change = changeText(indicator.value, indicator.prior_value, indicator.prefix);

          return (
            <Box
              key={indicator.key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: "8px 10px",
                bgcolor: "#F9FAFB",
                borderRadius: 2
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#243B53" }}>{indicator.label}</Typography>
                <Typography sx={{ fontSize: 9, color: "#9CA3AF" }}>{indicator.source}</Typography>
              </Box>

              <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#071f35" }}>
                  {formatIndicatorValue(indicator.value, indicator.prefix)}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "2px" }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color }}>{arrow}</Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color }}>{change}</Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {selectedIndicators.length === 0 && (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>No indicators selected</Typography>
        </Box>
      )}

      {selectedIndicators.length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid #E5E7EB" }}>
          <Typography sx={{ fontSize: 9, color: "#BCCCDC" }}>As of {selectedIndicators[0].date}</Typography>
        </Box>
      )}
    </Box>
  );
};
